import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Button,
} from '@monksflow/monks-ui';
import { ChevronDown, Check } from 'lucide-react';
import { SearchInput } from '@/shared/ui/SearchInput';

import {
  buildInsightTypeOptions,
  buildOpportunityTypeOptions,
} from './OpportunitiesRoomToolbar.constants';
import type { OpportunitiesRoomToolbarProps as Props } from './OpportunitiesRoomToolbar.types';
import { RadarlistToggle } from './RadarlistToggle';
import { displayMarketToken } from '@/entities/opportunity';
import { useTranslation } from 'react-i18next';

export function OpportunitiesRoomToolbarDesktop({
  filters,
  opportunityType,
  radarlistOnly,
  marketOptions,
  audienceOptions,
  onSearch,
  onInsightTypeChange,
  onOpportunityTypeChange,
  onMarketChange,
  onAudienceChange,
  onRadarlistOnlyChange,
}: Props) {
  const { t } = useTranslation('analysis');
  const insightTypeOptions = buildInsightTypeOptions();
  const opportunityTypeOptions = buildOpportunityTypeOptions();

  return (
    <div className="flex items-center justify-between gap-4">
      <RadarlistToggle radarlistOnly={radarlistOnly} onChange={onRadarlistOnlyChange} />

      {/* Search + filter chips */}
      <div className="flex items-center gap-3">
        <SearchInput
          value={filters.search}
          onChange={onSearch}
          placeholder={t('opportunitiesRoom.opportunitiesRoomToolbarDesktop.searchOpportunities')}
          aria-label={t(
            'opportunitiesRoom.opportunitiesRoomToolbarDesktop.searchOpportunitiesLabel',
          )}
          size="sm"
          className="w-80"
        />

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="max-w-62.5">
              <Button
                variant="outline"
                size="sm"
                className={filters.insightType ? 'bg-accent text-accent-foreground' : ''}
              >
                <span className="truncate">
                  {filters.insightType
                    ? insightTypeOptions.find((o) => o.value === filters.insightType)?.label
                    : t('opportunitiesRoom.opportunitiesRoomToolbarDesktop.allInsights')}
                </span>
                <ChevronDown className="ml-2 size-3.5 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-w-62.5">
              <DropdownMenuItem
                className="text-muted-foreground"
                onClick={() => onInsightTypeChange(null)}
              >
                {t('opportunitiesRoom.opportunitiesRoomToolbarDesktop.allInsights')}
                {!filters.insightType && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
              {insightTypeOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onInsightTypeChange(option.value)}
                >
                  {option.label}
                  {filters.insightType === option.value && <Check className="ml-auto size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild className="max-w-62.5">
              <Button
                variant="outline"
                size="sm"
                className={opportunityType ? 'bg-accent text-accent-foreground' : ''}
              >
                <span className="truncate">
                  {opportunityType
                    ? opportunityTypeOptions.find((o) => o.value === opportunityType)?.label
                    : t('opportunitiesRoom.opportunitiesRoomToolbarDesktop.allDuration')}
                </span>
                <ChevronDown className="ml-2 size-3.5 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-w-62.5 max-h-75 overflow-y-auto">
              <DropdownMenuItem
                className="text-muted-foreground"
                onClick={() => onOpportunityTypeChange(null)}
              >
                {t('opportunitiesRoom.opportunitiesRoomToolbarDesktop.allDuration')}
                {!opportunityType && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
              {opportunityTypeOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onOpportunityTypeChange(option.value)}
                >
                  {option.label}
                  {opportunityType === option.value && <Check className="ml-auto size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild className="max-w-62.5">
              <Button
                variant="outline"
                size="sm"
                className={filters.market ? 'bg-accent text-accent-foreground' : ''}
              >
                <span className="truncate">
                  {filters.market
                    ? displayMarketToken(filters.market)
                    : t('opportunitiesRoom.opportunitiesRoomToolbarDesktop.allMarkets')}
                </span>
                <ChevronDown className="ml-2 size-3.5 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-w-62.5">
              <DropdownMenuItem
                className="text-muted-foreground"
                onClick={() => onMarketChange(null)}
              >
                {t('opportunitiesRoom.opportunitiesRoomToolbarDesktop.allMarkets')}
                {!filters.market && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
              {marketOptions.map((market) => (
                <DropdownMenuItem key={market} onClick={() => onMarketChange(market)}>
                  {displayMarketToken(market)}
                  {filters.market === market && <Check className="ml-auto size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild className="max-w-62.5">
              <Button
                variant="outline"
                size="sm"
                className={filters.audience ? 'bg-accent text-accent-foreground' : ''}
              >
                <span className="truncate">
                  {filters.audience ||
                    t('opportunitiesRoom.opportunitiesRoomToolbarDesktop.allAudiences')}
                </span>
                <ChevronDown className="ml-2 size-3.5 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-w-62.5">
              <DropdownMenuItem
                className="text-muted-foreground"
                onClick={() => onAudienceChange(null)}
              >
                {t('opportunitiesRoom.opportunitiesRoomToolbarDesktop.allAudiences')}
                {!filters.audience && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
              {audienceOptions.map((audience) => (
                <DropdownMenuItem key={audience} onClick={() => onAudienceChange(audience)}>
                  {audience}
                  {filters.audience === audience && <Check className="ml-auto size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
